import Vue from 'vue';
import Vuex from 'vuex';
import _ from 'lodash';

import * as fb from '../firebase';
import router from '../router/index';

Vue.use(Vuex);

// TUTO Vuex:
// this.$store.dispatch('func', data);
// actions -> mutations

const store = new Vuex.Store({
  state: {
    userProfile: null,
    currentWorkspace: null,
    currentWidth: null,
    isTouchDevice: false,
    isPlannedMealDragged: false,
    /**
     * Quand un utilisateur non connecté doit rejoindre un workspace,
     * On retient ce workspace (au lieu d'en créer un)
     */
    workspaceIdToJoin: null,
  },

  // MUTATIONS
  mutations: {
    setUserProfile(state, val) {
      state.userProfile = val;
    },
    setCurrentWorkspace(state, workspace) {
      state.currentWorkspace = workspace;
    },
    addUserWorkspace(state, workspace) {
      // add unless already in
      if (state.userProfile && state.userProfile.workspaces) {
        const index = _.findIndex(
          state.userProfile.workspaces,
          (ws) => ws.id === workspace.id
        );
        if (index === -1) {
          // Not found, we push
          state.userProfile.workspaces.push(workspace);
        } else {
          // Found, we update
          state.userProfile.workspaces[index] = workspace;
        }
      }
    },
    setCurrentWidth(state, width) {
      state.currentWidth = width;
    },
    setTouchDevice(state, isTouchDevice) {
      state.isTouchDevice = isTouchDevice;
    },
    setIsPlannedMealDragged(state, isDragged) {
      state.isPlannedMealDragged = isDragged;
    },
    setWorkspaceIdToJoin(state, workspaceIdToJoin) {
      state.workspaceIdToJoin = workspaceIdToJoin;
    },

    /**
     * Utilisé pour la déconnexion
     * @param {*} state
     */
    clearStore(state) {
      state.userProfile = null;
      state.currentWorkspace = null;
      state.workspaceIdToJoin = null;
    },
  },
  actions: {
    async fetchUserProfile({ commit, dispatch, state }, user) {
      // On modifie à choque modif
      fb.usersCollection.doc(user.uid).onSnapshot((doc) => {
        if (!doc.exists) {
          // Problème de connexion, on redirige vers login
          router.push('/login');
          return;
        }
        // set user profile in state
        const data = doc.data();
        const workspaces = _.cloneDeep(_.get(data, 'workspaces', []));
        // We do workspaces later
        data.workspaces = [];
        commit('setUserProfile', data);

        // WORKSPACES
        workspaces.forEach(async (w) => {
          const workspace = await fb.workspacesCollection.doc(w.id).get();
          if (workspace.exists) {
            const ws = workspace.data();
            ws.id = workspace.id;
            commit('addUserWorkspace', ws);
          }
        });

        // Maintenant on vérifie que le current n'a pas été supprimé
        const workspacesId = workspaces.map((w) => w.id);
        // Vérif dans le local storage
        let localCurrentWorkspace = localStorage.currentWorkspace;
        // on vérifie que le workspace en mémoire n'a pas été supprimé
        if (!workspacesId.includes(localCurrentWorkspace)) {
          localCurrentWorkspace = null;
        }

        let forceChangeWorkspace = false;
        // Vérif dans state
        if (
          state.currentWorkspace &&
          _.findIndex(
            workspacesId,
            _.get(state.currentWorkspace, 'id', null)
          ) === -1
        ) {
          forceChangeWorkspace = true;
        }

        if (forceChangeWorkspace || state.currentWorkspace === null) {
          // Si nul, on reprend celui de la mémoire puis celui par défaut
          const defaultWorkspaceId = _.get(workspaces, '[0].id', null);
          // Set local, else set the first one
          dispatch('setCurrentWorkspace', {
            workspaceId: localCurrentWorkspace,
            fallbackWorkspaceId: defaultWorkspaceId,
          });
        }
      });

      // change route to dashboard
      if (router.currentRoute.path === '/login') {
        // If should join workspace
        if (state.workspaceIdToJoin) {
          router.push(`/join/${state.workspaceIdToJoin}`);
          // Delete workspaceIdToJoin
          dispatch('setWorkspaceIdToJoin', null);
        } else {
          router.push('/');
        }
      }
    },
    async setCurrentWorkspace({ commit }, data) {
      let workspaceId = null;
      let fallbackWorkspaceId = null;
      if (typeof data === typeof {}) {
        // Si objet, on peut donner un fallback
        workspaceId = _.get(data, 'workspaceId', null);
        fallbackWorkspaceId = _.get(data, 'fallbackWorkspaceId', null);
      } else {
        // Sinon le data est l'id
        workspaceId = data;
      }

      let workspace = null;
      if (workspaceId) {
        workspace = await fb.workspacesCollection.doc(workspaceId).get();
      }
      if (_.isNil(workspace) && fallbackWorkspaceId) {
        // Au cas où, default
        workspace = await fb.workspacesCollection
          .doc(fallbackWorkspaceId)
          .get();
      }
      // Maj the localStorage
      if (_.isNil(workspace)) {
        console.error('No workspace set');
      }
      localStorage.currentWorkspace = workspace.id;
      commit('setCurrentWorkspace', {
        id: workspace.id,
        ref: workspace.ref,
        ...workspace.data(),
      });
    },

    async logout({ commit }) {
      await fb.auth.signOut();

      // clear userProfile and redirect to /login
      localStorage.clear();
      commit('clearStore');
      router.push('/login');
    },
    async updateProfile({ dispatch }, user) {
      const userId = fb.auth.currentUser.uid;
      // update user object
      await fb.usersCollection.doc(userId).update({
        name: user.name,
      });

      dispatch('fetchUserProfile', { uid: userId });
    },

    async setCurrentWidth({ commit }, width) {
      commit('setCurrentWidth', width);
    },

    async setTouchDevice({ commit }, isTouchDevice) {
      commit('setTouchDevice', isTouchDevice);
    },

    async setIsPlannedMealDragged({ commit }, isDragged) {
      commit('setIsPlannedMealDragged', isDragged);
    },

    async setWorkspaceIdToJoin({ commit }, workspaceIdToJoin) {
      commit('setWorkspaceIdToJoin', workspaceIdToJoin);
    },
  },
  modules: {},
});

export default store;
